import { DebugType, IExecutionOptions, IQuery, IQueryContext, IQueryOptions } from '@kurtosys/types/query/index.js';

import { isQueryOptionsObject } from '../common/commonUtils/isQueryOptionsObject.js';
import { deepMergeObjects } from '../common/commonUtils/object.js';
import { isUndefined } from '../common/commonUtils/typeChecks.js';
import { helpers } from '../common/index.js';

import { ExternalApiQuery } from './externalApi/ExternalApiQuery.js';
import { AllocationsQuery } from './queries/AllocationsQuery.js';
import { ArticleQuery } from './queries/ArticleQuery.js';
import { AssetQuery } from './queries/AssetQuery.js';
import { BaseQuery } from './queries/base/BaseQuery.js';
import { BreakdownAnalysisQuery } from './queries/BreakdownAnalysisQuery.js';
import { ClientConfigurationQuery } from './queries/ClientConfigurationQuery.js';
import { CommentaryQuery } from './queries/CommentaryQuery.js';
import { CoursesQuery } from './queries/CoursesQuery.js';
import { DatasetQuery } from './queries/DatasetQuery.js';
import { DisclaimerQuery } from './queries/DisclaimerQuery.js';
import { DocumentEntityQuery } from './queries/DocumentEntityQuery.js';
import { DocumentQuery } from './queries/DocumentQuery.js';
import { DynamicQuery } from './queries/DynamicQuery.js';
import { EntitiesQuery } from './queries/EntitiesQuery.js';
import { FundListsQuery } from './queries/FundListsQuery.js';
import { FundManagerQuery } from './queries/FundManagerQuery.js';
import { FundManagersQuery } from './queries/FundManagersQuery.js';
import { GlobalQuery } from './queries/GlobalQuery.js';
import { HistoricalDatasetsQuery } from './queries/HistoricalDatasetsQuery.js';
import { InputQuery } from './queries/InputQuery.js';
import { JoinQuery } from './queries/JoinQuery.js';
import { LoggedInUserQuery } from './queries/LoggedInUserQuery.js';
import { ObjectQuery } from './queries/ObjectQuery.js';
import { PortfolioEntityQuery } from './queries/PortfolioEntityQuery.js';
import { PortfolioQuery } from './queries/PortfolioQuery.js';
import { PropertyQuery } from './queries/PropertyQuery.js';
import { ResponseQuery } from './queries/ResponseQuery.js';
import { ScenarioQuery } from './queries/ScenarioQuery.js';
import { ScenarioReportQuery } from './queries/ScenarioReportQuery.js';
import { ScenarioStatisticQuery } from './queries/ScenarioStatisticQuery.js';
import { SingleDocumentQuery } from './queries/SingleDocumentQuery.js';
import { StatisticQuery } from './queries/StatisticQuery.js';
import { StatisticsQuery } from './queries/StatisticsQuery.js';
import { TimeseriesQuery } from './queries/TimeseriesQuery.js';
import { TransactionsQuery } from './queries/TransactionsQuery.js';
import { UrlQuery } from './queries/UrlQuery.js';
import { UserConfigurationQuery } from './queries/UserConfigurationQuery.js';
import { ValueQuery } from './queries/ValueQuery.js';
import { WindowQuery } from './queries/WindowQuery.js';
import { WordpressGlobalSearchContentQuery } from './queries/WordpressGlobalSearchContentQuery.js';
import { Transform } from './transform/Transform.js';

export class Query implements IQuery {
	constructor(
		public options: IQueryOptions,
		public translationHelper?: helpers.TranslationHelper,
		public callStackLevel?: number,
	) {}

	private debugBase(type: DebugType, ...messages: any[]): void {
		if (this.options && this.options.debug && this.options.debug.key) {
			const prefix = `QueryOptions Debug - "${this.options.debug.key}": `;
			console[type](prefix, ...messages);
		}
	}
	debug = {
		error: (...messages: any[]): void => {
			this.debugBase(DebugType.error, ...messages);
		},
		info: (...messages: any[]): void => {
			this.debugBase(DebugType.info, ...messages);
		},
		warn: (...messages: any[]): void => {
			this.debugBase(DebugType.warn, ...messages);
		},
	};
	execute(executionOptions: IExecutionOptions): any {
		this.debug.info('Execute Started', { options: this.options });
		// in the case that the QueryOptions passed has already been converted, we will just return it as is.
		if (!isQueryOptionsObject(this.options)) {
			this.debug.info('Already converted to a value, options are not of type IQueryOptions');
			return this.options;
		}

		const { queryOptionsType, value, separators = [], keyQuery, codeQuery } = this.options;

		if (keyQuery) {
			this.debug.info('keyQuery defined', { keyQuery });
			const query = new Query(keyQuery, this.translationHelper);
			this.options.key = query.execute(executionOptions);
			this.debug.info('key = ', this.options.key);
		}

		if (codeQuery) {
			this.debug.info('codeQuery defined', { codeQuery });
			const query = new Query(codeQuery, this.translationHelper);
			this.options.code = query.execute(executionOptions);
			this.debug.info('code = ', this.options.code);
		}

		let query: BaseQuery | undefined;

		const dependencies = { queryClass: Query, transformClass: Transform };

		if (!isUndefined(value)) {
			query = new ValueQuery(dependencies, this.options, this.translationHelper);
		} else if (queryOptionsType) {
			switch (queryOptionsType) {
				case 'response':
					query = new ResponseQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'dynamic':
					query = new DynamicQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'input':
					query = new InputQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'property':
					query = new PropertyQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'allocations':
					query = new AllocationsQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'historicalDatasets':
					query = new HistoricalDatasetsQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'statistics':
					query = new StatisticsQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'statistic':
					query = new StatisticQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'timeseries':
					query = new TimeseriesQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'fundManager':
					query = new FundManagerQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'fundManagers':
					query = new FundManagersQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'commentaries':
					query = new CommentaryQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'disclaimers':
					query = new DisclaimerQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'documents':
					query = new DocumentQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'document':
					query = new SingleDocumentQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'fundLists':
					query = new FundListsQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'object':
					query = new ObjectQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'global':
					query = new GlobalQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'clientConfiguration':
					query = new ClientConfigurationQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'userConfiguration':
					query = new UserConfigurationQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'url':
					query = new UrlQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'window':
					query = new WindowQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'join':
					query = new JoinQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'scenario':
					query = new ScenarioQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'scenarioReport':
					query = new ScenarioReportQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'portfolio':
					query = new PortfolioQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'portfolioEntity':
					query = new PortfolioEntityQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'asset':
					query = new AssetQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'scenarioStatistic':
					query = new ScenarioStatisticQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'entities':
					query = new EntitiesQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'breakdownAnalysis':
					query = new BreakdownAnalysisQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'loggedInUser':
					query = new LoggedInUserQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'externalApi':
					query = new ExternalApiQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'transactions':
					query = new TransactionsQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'none':
					query = new ValueQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'documentEntity':
					query = new DocumentEntityQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'article':
					query = new ArticleQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'courses':
					query = new CoursesQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'dataset':
					query = new DatasetQuery(dependencies, this.options, this.translationHelper);
					break;
				case 'wordpressGlobalSearch':
					query = new WordpressGlobalSearchContentQuery(dependencies, this.options, this.translationHelper);
			}
		}
		if (query) {
			let queryResponse = query.execute(executionOptions, this.callStackLevel);
			if (queryResponse && typeof queryResponse === 'string' && separators && separators.length > 0) {
				queryResponse = this.stripWrappingSeparators(queryResponse, separators);
			}
			return queryResponse;
		}
	}

	stripWrappingSeparators(queryResponse: string, separators: string[]): string {
		for (let separatorIndex = 0; separatorIndex < separators.length; separatorIndex++) {
			const separator = separators[separatorIndex];
			const separtorLength = separator.length;
			while (queryResponse.startsWith(separator)) {
				queryResponse = (queryResponse as string).substr(separtorLength);
			}
			while (queryResponse.endsWith(separator)) {
				queryResponse = (queryResponse as string).substr(0, queryResponse.length - separtorLength);
			}
		}
		return queryResponse;
	}

	static mergeExecutionOptions(...args: (IExecutionOptions | undefined)[]): IExecutionOptions {
		let response: IExecutionOptions = {};
		for (const arg of args) {
			if (arg) {
				const { context: fullContext, ...executionOptions } = arg;
				const { entityByType, ...context } = (fullContext || {}) as IQueryContext;
				const hasContext = typeof context === 'object' && Object.keys(context).length > 0;
				response = deepMergeObjects(response, executionOptions, hasContext ? { context } : {});
				if (entityByType) {
					if (!response.context) {
						response.context = {};
					}
					response.context.entityByType = entityByType;
				}
			}
		}
		return response;
	}
}
